import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Video, 
  Users, 
  ChevronLeft
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import CollapsibleSidebar from './CollapsibleSidebar';

interface GuestViewer {
  email:  string;
  name:   string;
  photo?: string | null;
}

interface SharedVideoLayoutProps {
  children:     React.ReactNode;
  onBack?:      () => void;
  guestViewer?: GuestViewer | null;
}

const SharedVideoLayout: React.FC<SharedVideoLayoutProps> = ({ children, onBack, guestViewer }) => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
    navigate('/');
  };

  const menuItems = [
    { id: 'for-you', label: t('for_you_menu_item'), icon: Users },
    { id: 'library', label: t('shared_layout.menu_items.library'), icon: Video },
    // { id: 'meetings', label: t('meetings'), icon: Calendar, badge: t('new_badge') },
    // { id: 'notifications', label: t('notifications'), icon: Bell },
    // { id: 'watch-later', label: t('watch_later'), icon: Clock },
    // { id: 'history', label: t('history'), icon: History },
    // { id: 'earn-videos', label: t('earn_free_videos'), icon: Gift, badge: t('new_badge') },
    // { id: 'settings', label: t('settings'), icon: Settings },
  ];

  return (
    <div className="flex h-screen t-page">
      {/* Sidebar */}
      <CollapsibleSidebar 
        className="flex-shrink-0"
        onNavigate={(tab) => navigate(`/${tab === 'for-you' ? '' : tab}`)}
        sidebarType="shared"
      >
        <div className="p-4 border-b t-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--button-primary-bg, #4f46e5)',
                  color: 'var(--button-primary-foreground, #ffffff)',
                }}
              >
                <Video className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold t-text">{t('shared_layout.app_name')}</span>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="py-4">
          <button
            onClick={onBack || (() => navigate('/'))}
            className="w-full flex items-center gap-3 px-4 py-2 text-left t-hover t-text"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>{t('shared_layout.back_to_home')}</span>
          </button>
          
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(`/${item.id === 'for-you' ? '' : item.id}`)}
              className="w-full flex items-center gap-3 px-4 py-2 text-left t-hover t-text"
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
            </button>
          ))}
        </nav>
      </CollapsibleSidebar>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="t-surface border-b t-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={onBack || (() => navigate('/'))}
                className="md:hidden p-2 t-hover rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 t-muted" />
              </button>
              <h1 className="text-xl font-semibold t-text">{t('shared_layout.shared_video_header')}</h1>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              {/* Guest viewer verified via email — show their org avatar */}
              {!user && guestViewer ? (
                <div className="flex items-center gap-2">
                  {guestViewer.photo ? (
                    <img
                      src={guestViewer.photo}
                      alt={guestViewer.name}
                      title={`${guestViewer.name} · ${guestViewer.email}`}
                      style={{
                        width: 32, height: 32, borderRadius: '50%',
                        objectFit: 'cover', border: '2px solid var(--border, #e5e7eb)',
                      }}
                    />
                  ) : (
                    <div
                      title={`${guestViewer.name} · ${guestViewer.email}`}
                      style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'var(--primary, #4f46e5)',
                        color: 'var(--button-primary-foreground, #fff)', fontWeight: 700, fontSize: 13,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {guestViewer.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span style={{ fontSize: 13, color: 'var(--foreground, #111)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {guestViewer.name}
                  </span>
                </div>
              ) : user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-colors"
                    style={{
                      backgroundColor: 'var(--button-primary-bg, #4f46e5)',
                      color: 'var(--button-primary-foreground, #ffffff)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--button-primary-hover-bg, #4338ca)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--button-primary-bg, #4f46e5)';
                    }}
                  >
                    {localStorage.getItem('userEmail')?.charAt(0).toUpperCase() || 'U'}
                  </button>
                  
                  {showUserMenu && (
                    <div className="absolute right-0 top-10 t-surface border t-border rounded-lg shadow-lg z-10 min-w-[200px]">
                      <div className="p-3 border-b t-border">
                        <p className="text-sm font-medium t-text truncate">
                          {localStorage.getItem('userEmail') || 'User'}
                        </p>
                      </div>
                      <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-2 text-left text-sm t-text t-hover flex items-center gap-2"
                      >
                        <span>{t('shared_layout.sign_out')}</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // On /share/ routes there is no session — hide the Sign In button entirely
                window.location.pathname.startsWith('/share/') ? null : (
                  <button
                    onClick={() => {
                      const sharePath = window.location.pathname + window.location.search;
                      if (sharePath.startsWith('/share/')) {
                        sessionStorage.setItem('airecording_returnTo', sharePath);
                      }
                      navigate('/login');
                    }}
                    className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                    style={{
                      backgroundColor: 'var(--button-primary-bg, #4f46e5)',
                      color: 'var(--button-primary-foreground, #ffffff)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--button-primary-hover-bg, #4338ca)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--button-primary-bg, #4f46e5)';
                    }}
                  >
                    <span>{t('shared_video.sign_in', 'Sign in')}</span>
                  </button>
                )
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default SharedVideoLayout;
